/**
 * Audio Service - Global audio manager for seamless playback across routes
 * Replaces p5.sound to maintain audio playback during route transitions
 */

let audioElement = null;
let isLoaded = false;
let volumeFadeAnimation = null;

export const audioService = {
  /**
   * Load an audio file
   * @param {string} audioPath - Path to audio file
   * @returns {Promise<void>}
   */
  async load(audioPath) {
    if (!audioElement) {
      // First time load - create new audio element
      audioElement = new Audio(audioPath);
      audioElement.loop = false; // Don't loop by default

      // Wait for audio to be ready to play
      await new Promise((resolve, reject) => {
        audioElement.addEventListener('canplaythrough', () => {
          console.log('audioService: audio loaded');
          isLoaded = true;
          resolve();
        }, { once: true });

        audioElement.addEventListener('error', (error) => {
          console.error('audioService: error loading audio', error);
          reject(error);
        }, { once: true });

        // Start loading
        audioElement.load();
      });
    } else {
      // Subsequent loads - reset state for replay
      audioElement.currentTime = 0;
      audioElement.volume = 1;
      console.log('audioService: audio reset for replay');
    }

    return audioElement;
  },

  /**
   * Play the audio
   */
  play() {
    if (audioElement && isLoaded && audioElement.paused) {
      console.log('audioService: playing sound');
      audioElement.play().catch(err => {
        console.error('audioService: play error', err);
      });
    }
  },

  /**
   * Pause the audio with optional volume fade
   * @param {number} fadeTime - Time in milliseconds to fade out (default: 0)
   */
  pause(fadeTime = 0) {
    if (!audioElement || audioElement.paused) return;

    if (fadeTime > 0) {
      // Fade out then pause, then reset volume for next play
      this.setVolume(0, fadeTime).then(() => {
        audioElement.pause();
        // Reset volume so next play() starts at full volume
        audioElement.volume = 1;
        console.log('audioService: pausing sound (with fade)');
      });
    } else {
      audioElement.pause();
      console.log('audioService: pausing sound');
    }
  },

  /**
   * Stop the audio and reset playhead
   * @param {number} fadeTime - Time in milliseconds to fade out before stopping (default: 0)
   */
  stop(fadeTime = 0) {
    if (!audioElement) return;

    if (fadeTime > 0) {
      // Fade to 0, then pause and reset
      this.setVolume(0, fadeTime).then(() => {
        audioElement.pause();
        audioElement.currentTime = 0;
        // Reset volume for next play
        audioElement.volume = 1;
        console.log('audioService: stopped with fade');
      });
    } else {
      // Immediate stop
      audioElement.pause();
      audioElement.currentTime = 0;
      audioElement.volume = 1;  // Reset volume for next play
      console.log('audioService: stopped immediately');
    }
  },

  /**
   * Set volume with optional smooth fade
   * @param {number} targetVolume - Target volume (0-1)
   * @param {number} fadeTime - Time in milliseconds to fade (default: 0)
   * @returns {Promise<void>}
   */
  setVolume(targetVolume, fadeTime = 0) {
    if (!audioElement) return Promise.resolve();

    // Cancel any existing fade animation
    if (volumeFadeAnimation) {
      cancelAnimationFrame(volumeFadeAnimation);
      volumeFadeAnimation = null;
    }

    // Clamp volume between 0 and 1
    targetVolume = Math.max(0, Math.min(1, targetVolume));

    if (fadeTime <= 0) {
      // Instant volume change
      audioElement.volume = targetVolume;
      return Promise.resolve();
    }

    // Smooth volume fade
    return new Promise((resolve) => {
      const startVolume = audioElement.volume;
      const startTime = Date.now();

      const fade = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / fadeTime, 1);

        // Linear interpolation
        audioElement.volume = startVolume + (targetVolume - startVolume) * progress;

        if (progress < 1) {
          volumeFadeAnimation = requestAnimationFrame(fade);
        } else {
          volumeFadeAnimation = null;
          resolve();
        }
      };

      fade();
    });
  },

  /**
   * Check if audio is currently playing
   * @returns {boolean}
   */
  isPlaying() {
    return audioElement && !audioElement.paused;
  },

  /**
   * Get the current audio element (for advanced use)
   * @returns {HTMLAudioElement|null}
   */
  getAudioElement() {
    return audioElement;
  }
};
